/**
 * Whether the person signed out on purpose, so the next sign-in must ask for credentials.
 *
 * Each tab keeps its own tokens and loses them when it closes. The identity provider's SSO session
 * outlives the tab, so a reopened tab signs in without credentials unless this marker is set. Sign out
 * sets it to a fresh id (localStorage, so a tab opened after the logout sees it). A sign-in clears it
 * only when that sign-in was started with this very id, so a silent sign-in that was already under
 * way in another tab cannot erase a newer logout. Storage that cannot be read counts as set: the
 * guard fails closed.
 *
 * @module client/Auth/signedOutMarker
 */

const KEY = 'outlaw:auth:v1:signed-out';
/** The marker id this tab's forced sign-in was started under; tab-local, so it survives the redirect. */
const ACK_KEY = 'outlaw:auth:v1:signed-out-ack';

/** Set the marker. Returns false when it could not be stored and read back, so the caller can say so. */
export function markSignedOut(): boolean {
    try {
        const id = crypto.randomUUID();
        window.localStorage.setItem(KEY, id);
        return window.localStorage.getItem(KEY) === id;
    } catch {
        return false;
    }
}

/** True when the next sign-in must show the provider's login screen. */
export function requiresFreshLogin(): boolean {
    try { return window.localStorage.getItem(KEY) !== null; } catch { return true; }
}

/** Called when a forced sign-in starts: remember which logout it answers. */
export function acknowledgeSignedOut(): void {
    try {
        const id = window.localStorage.getItem(KEY);
        if (id !== null) window.sessionStorage.setItem(ACK_KEY, id);
    } catch { /* the marker then stays set, which is the safe side */ }
}

/** Called after a sign-in completes: clear the marker only if this sign-in answered it. */
export function clearSignedOut(): void {
    try {
        const acknowledged = window.sessionStorage.getItem(ACK_KEY);
        window.sessionStorage.removeItem(ACK_KEY);
        if (acknowledged !== null && window.localStorage.getItem(KEY) === acknowledged) {
            window.localStorage.removeItem(KEY);
        }
    } catch { /* the marker then stays set, which is the safe side */ }
}
